import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BopRowActions, { RowIcon } from '../../../../components/BopRowActions';
import { apiFetch } from '../../../../lib/api';

// Codici con cui la catena identifica i nostri articoli nei propri ordini.
// Base del matching righe dell'import ordini GO.

interface CodiceArticoloInsegna {
  id: number;
  codiceArticoloInsegna: string;
  articoloId: number;
  articolo: {
    id: number;
    codice: string;
    descrizioneBase: string;
    ean: string | null;
    marca: { nome: string } | null;
  };
}

interface ArticoloOpzione {
  id: number;
  codice: string;
  descrizioneBase: string;
  ean: string | null;
}

interface Props {
  insegnaId: number;
  canEdit: boolean;
  canDelete: boolean;
  /** Chiamata dopo un'aggiunta o un'eliminazione, per aggiornare i conteggi della scheda. */
  onCambio: () => void;
}

export default function TabCodiciArticoloInsegna({ insegnaId, canEdit, canDelete, onCambio }: Props) {
  const [codici, setCodici] = useState<CodiceArticoloInsegna[]>([]);
  const [articoli, setArticoli] = useState<ArticoloOpzione[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [filtro, setFiltro] = useState('');
  const [nuovoCodice, setNuovoCodice] = useState('');
  const [nuovoArticoloId, setNuovoArticoloId] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      // @api GET /api/anagrafiche/insegne/:id/codici-articolo -> {"success":true,"data":[{"id":1,"codiceArticoloInsegna":"410007660","articoloId":48,"articolo":{"id":48,"codice":"XST-SCOLAC60","descrizioneBase":"...","ean":null,"marca":{"nome":"Smartix"}}}]}
      const res = await apiFetch<{ success: boolean; data: CodiceArticoloInsegna[] }>(`/api/anagrafiche/insegne/${insegnaId}/codici-articolo`);
      setCodici(res.data || []);
      if (canEdit) {
        // @api GET /api/anagrafiche/insegne/:id/articoli-smartix?q= -> {"success":true,"data":[{"id":48,"codice":"XST-SCOLAC60","descrizioneBase":"...","ean":null}]}
        const resArt = await apiFetch<{ success: boolean; data: ArticoloOpzione[] }>(`/api/anagrafiche/insegne/${insegnaId}/articoli-smartix`);
        setArticoli(resArt.data || []);
      }
    } catch (err: any) {
      setErrore(err.message || 'Errore nel caricamento dei codici');
    } finally {
      setLoading(false);
    }
  }, [insegnaId, canEdit]);

  useEffect(() => { carica(); }, [carica]);

  const aggiungi = async () => {
    if (!nuovoArticoloId) { setErrore("Seleziona l'articolo"); return; }
    if (!nuovoCodice.trim()) { setErrore("Inserisci il codice dell'insegna"); return; }
    setSalvando(true);
    setErrore('');
    try {
      // @api POST /api/anagrafiche/insegne/:id/codici-articolo {"articoloId":48,"codiceArticoloInsegna":"410007660"} -> {"success":true,"data":{"id":1,"codiceArticoloInsegna":"410007660","articoloId":48,"articolo":{"id":48,"codice":"XST-SCOLAC60","descrizioneBase":"...","ean":null,"marca":{"nome":"Smartix"}}}}
      const res = await apiFetch<{ success: boolean; data: CodiceArticoloInsegna }>(
        `/api/anagrafiche/insegne/${insegnaId}/codici-articolo`,
        { method: 'POST', body: JSON.stringify({ articoloId: Number(nuovoArticoloId), codiceArticoloInsegna: nuovoCodice.trim() }) }
      );
      setCodici((prev) => [...prev, res.data].sort((a, b) => a.codiceArticoloInsegna.localeCompare(b.codiceArticoloInsegna)));
      setNuovoCodice('');
      setNuovoArticoloId('');
      onCambio();
    } catch (err: any) {
      setErrore(err.message || 'Errore nel salvataggio');
    } finally {
      setSalvando(false);
    }
  };

  const elimina = async (c: CodiceArticoloInsegna) => {
    if (!window.confirm(`Eliminare il codice "${c.codiceArticoloInsegna}" (${c.articolo.codice})?`)) return;
    setErrore('');
    try {
      // @api DELETE /api/anagrafiche/insegne/:id/codici-articolo/:codiceId -> {"success":true}
      await apiFetch(`/api/anagrafiche/insegne/${insegnaId}/codici-articolo/${c.id}`, { method: 'DELETE' });
      setCodici((prev) => prev.filter((x) => x.id !== c.id));
      onCambio();
    } catch (err: any) {
      setErrore(err.message || "Errore nell'eliminazione");
    }
  };

  const filtrati = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    if (!f) return codici;
    return codici.filter((c) =>
      c.codiceArticoloInsegna.toLowerCase().includes(f)
      || c.articolo.codice.toLowerCase().includes(f)
      || c.articolo.descrizioneBase.toLowerCase().includes(f)
      || (c.articolo.ean || '').toLowerCase().includes(f)
    );
  }, [codici, filtro]);

  return (
    <div className="bop-card">
      <div className="bop-card__header">
        <h3 className="bop-card__title">Codici articolo</h3>
        {codici.length > 0 && (
          <input
            className="bop-input bop-insegna-scheda__filtro"
            placeholder="Filtra per codice, articolo, descrizione o EAN…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        )}
      </div>
      <div className="bop-card__body">
        <p className="bop-text-muted bop-insegna-scheda__intro">
          Il codice con cui questa catena identifica i nostri articoli nei propri ordini.
          Vale per tutti i suoi punti vendita e si popola da solo al primo import:
          quando una riga si aggancia tramite il nostro codice o l'EAN, la corrispondenza
          viene memorizzata qui.
        </p>

        {canEdit && (
          <div className="bop-insegna-scheda__aggiungi">
            <select
              className="bop-select bop-insegna-scheda__aggiungi-articolo"
              value={nuovoArticoloId}
              onChange={(e) => { setNuovoArticoloId(e.target.value); setErrore(''); }}
              disabled={salvando || loading}
            >
              <option value="">— articolo Smartix —</option>
              {articoli.map((a) => (
                <option key={a.id} value={a.id}>{a.codice} — {a.descrizioneBase}</option>
              ))}
            </select>
            <input
              className="bop-input bop-insegna-scheda__aggiungi-codice"
              placeholder="Codice usato dall'insegna"
              value={nuovoCodice}
              onChange={(e) => { setNuovoCodice(e.target.value); setErrore(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !salvando) aggiungi(); }}
              disabled={salvando || loading}
            />
            <button className="bop-btn bop-btn-primary" onClick={aggiungi} disabled={salvando || loading}>
              {salvando ? 'Salvataggio…' : 'Aggiungi'}
            </button>
          </div>
        )}

        {canEdit && articoli.length === 0 && !loading && (
          <p className="bop-text-danger">
            Nessun articolo di marca Smartix in anagrafica: assegna la marca agli articoli
            prima di poter associare i codici.
          </p>
        )}

        {errore && <p className="bop-text-danger">{errore}</p>}

        {loading ? (
          <div className="bop-empty-state"><p className="bop-empty-state__text">Caricamento…</p></div>
        ) : codici.length === 0 ? (
          <div className="bop-empty-state">
            <p className="bop-empty-state__text">Nessun codice associato a questa insegna</p>
          </div>
        ) : filtrati.length === 0 ? (
          <div className="bop-empty-state">
            <p className="bop-empty-state__text">Nessun codice corrisponde al filtro</p>
          </div>
        ) : (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Codice insegna</th>
                <th>Articolo BOP</th>
                <th>Descrizione</th>
                <th>EAN</th>
                {canDelete && <th className="bop-text-right">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {filtrati.map((c) => (
                <tr key={c.id}>
                  <td><strong>{c.codiceArticoloInsegna}</strong></td>
                  <td><span className="bop-badge bop-badge-info">{c.articolo.codice}</span></td>
                  <td>{c.articolo.descrizioneBase}</td>
                  <td className="bop-text-muted">{c.articolo.ean || '—'}</td>
                  {canDelete && (
                    <td className="bop-text-right">
                      <BopRowActions
                        actions={[
                          { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => elimina(c), variant: 'danger' },
                        ]}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
